import { ControlValueAccessor, FormGroup, FormBuilder, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Component, OnInit, OnDestroy, Input, forwardRef } from "@angular/core";
import { Subscription } from "rxjs";

/**
 * A component that behaves similarly to the HTML <input type="password"> element,
 * but this component exists to prevent browsers from trying to auto-fill passwords
 * into such elements as soon as the page loads.
 */
@Component({
	selector: 'app-obscured-input',
	templateUrl: './obscured-input.component.html',
	styleUrls: ['./obscured-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ObscuredInputComponent),
			multi: true
		}
	]
})
export class ObscuredInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
	/**
	 * The label to use for the mat-form-field.
	 */
	@Input()
	public label: string = '';

	/**
	 * The width to apply to the mat-form-field.
	 */
	@Input()
	public width?: string;

	/**
	 * The form that contains the value being entered.
	 */
	public form!: FormGroup;

	private _propagateChanges?: (value: string) => void;
	private _valueChangesSubscription?: Subscription;

	constructor(
		private _formBuilder: FormBuilder
	) { }

	public ngOnInit(): void {
		this.form = this.buildForm();

		if (this._valueChangesSubscription) {
			this._valueChangesSubscription.unsubscribe();
		}
		this._valueChangesSubscription = this.createValueChangesSubscription();
	}

	public ngOnDestroy(): void {
		if (this._valueChangesSubscription) {
			this._valueChangesSubscription.unsubscribe();
		}
	}

	/**
	 * Implements ControlValueAccessor.writeValue.
	 */
    public writeValue(value: string): void {
		this.form.patchValue({
			'value': value
		});
	}

	/**
	 * Implements ControlValueAccessor.registerOnChange.
	 */
	public registerOnChange(fn: (value: any) => void): void {
		this._propagateChanges = fn;
	}

	/**
	 * Implements ControlValueAccessor.registerOnTouched.
	 */
    public registerOnTouched(fn: any): void {
	}

	/**
	 * Builds the main form group.
	 */
	private buildForm(): FormGroup {
		return this._formBuilder.group({
			value: ['']
		});
	}

	/**
	 * Creates a subscription that propagates the form's value
	 * whenever the form emits valueChanges.
	 */
	private createValueChangesSubscription(): Subscription {
		return this.form.get('value')!.valueChanges.subscribe((value: string) => {
			if (this._propagateChanges) {
				this._propagateChanges(value);
			}
		});
	}
}